import { Component, OnInit } from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import { UserSessionService } from '../../services/user-session.service';
import {
  ControlDossieCase,
  ControlDossieEvidence,
  ControlDossieService,
  ControlDossieSummary
} from '../../services/controldossie.service';

@Component({
  selector: 'app-controldossie',
  templateUrl: './controldossie.component.html',
  styleUrl: './controldossie.component.scss'
})
export class ControlDossieComponent implements OnInit {
  tenantId: number | null = null;
  userId: number | null = null;
  canAccess = false;
  loading = false;
  saving = false;
  analyzing = false;
  editingReport = false;
  reportDraft = '';
  analysisGuidanceDraft = '';
  searchTerm = '';
  statusFilter = '';
  summary: ControlDossieSummary = { total: 0, collecting: 0, ready: 0, organized: 0 };
  dossies: ControlDossieCase[] = [];
  selectedDossie: ControlDossieCase | null = null;
  evidences: ControlDossieEvidence[] = [];

  showDossieModal = false;
  editingDossie: ControlDossieCase | null = null;
  dossieForm = this.emptyDossieForm();

  showEvidenceModal = false;
  evidenceFiles: File[] = [];
  evidenceForm = this.emptyEvidenceForm();

  readonly statusLabels: Record<string, string> = {
    DRAFT: 'Rascunho',
    COLLECTING: 'Coletando',
    READY_FOR_ANALYSIS: 'Pronto para análise',
    ANALYZING: 'Analisando',
    ORGANIZED: 'Organizado',
    FINALIZED: 'Finalizado',
    ARCHIVED: 'Arquivado'
  };

  readonly evidenceTypeLabels: Record<string, string> = {
    TEXT: 'Texto',
    IMAGE: 'Print / imagem',
    AUDIO: 'Áudio',
    VIDEO: 'Vídeo',
    PDF: 'PDF',
    DOCUMENT: 'Documento',
    LINK: 'Link',
    OTHER: 'Outro'
  };

  constructor(
    private userSession: UserSessionService,
    private dossieService: ControlDossieService,
    private snackBar: MatSnackBar
  ) {}

  ngOnInit() {
    const user = this.userSession.getUser();
    this.tenantId = Number(user?.tenant_id || 0) || null;
    this.userId = Number(user?.id || 0) || null;
    this.canAccess = this.isAllowedProfile(user);
    if (!this.canAccess || !this.tenantId || !this.userId) {
      return;
    }
    this.loadAll();
  }

  loadAll() {
    this.loadDashboard();
    this.loadDossies();
  }

  loadDashboard() {
    if (!this.tenantId || !this.userId) return;
    this.dossieService.dashboard(this.tenantId, this.userId).subscribe({
      next: (summary) => {
        this.summary = {
          total: Number(summary?.total || 0),
          collecting: Number(summary?.collecting || 0),
          ready: Number(summary?.ready || 0),
          organized: Number(summary?.organized || 0)
        };
      },
      error: () => this.showMessage('Não foi possível carregar o resumo.')
    });
  }

  loadDossies() {
    if (!this.tenantId || !this.userId) return;
    this.loading = true;
    this.dossieService.listDossies(this.tenantId, this.userId, {
      q: this.searchTerm,
      status: this.statusFilter,
      page_size: 20
    }).subscribe({
      next: (response) => {
        this.dossies = response.rows || [];
        if (this.selectedDossie) {
          const refreshed = this.dossies.find((item) => item.id === this.selectedDossie?.id);
          if (refreshed) {
            this.selectedDossie = { ...this.selectedDossie, ...refreshed };
          }
        }
      },
      error: () => this.showMessage('Não foi possível listar os dossiês.'),
      complete: () => this.loading = false
    });
  }

  selectDossie(dossie: ControlDossieCase) {
    this.selectedDossie = dossie;
    this.editingReport = false;
    this.reportDraft = dossie.final_report || '';
    this.analysisGuidanceDraft = dossie.analysis_guidance || '';
    this.loadEvidences();
  }

  backToList() {
    this.selectedDossie = null;
    this.evidences = [];
    this.editingReport = false;
    this.reportDraft = '';
    this.analysisGuidanceDraft = '';
  }

  loadEvidences() {
    if (!this.tenantId || !this.userId || !this.selectedDossie) return;
    this.dossieService.listEvidences(this.tenantId, this.userId, this.selectedDossie.id).subscribe({
      next: (rows) => this.evidences = rows || [],
      error: () => this.showMessage('Não foi possível carregar as evidências.')
    });
  }

  refreshSelectedDossie() {
    if (!this.tenantId || !this.userId || !this.selectedDossie) return;
    const selectedId = this.selectedDossie.id;
    this.dossieService.getDossie(this.tenantId, this.userId, selectedId).subscribe({
      next: (dossie) => {
        this.selectedDossie = dossie;
        this.analysisGuidanceDraft = dossie.analysis_guidance || this.analysisGuidanceDraft || '';
        const index = this.dossies.findIndex((item) => item.id === dossie.id);
        if (index >= 0) {
          this.dossies[index] = { ...this.dossies[index], ...dossie };
        }
      },
      error: () => this.loadDossies()
    });
  }

  openDossieModal(dossie?: ControlDossieCase) {
    this.editingDossie = dossie || null;
    this.dossieForm = dossie
      ? {
          title: dossie.title,
          objective: dossie.objective,
          evidence_guidance: dossie.evidence_guidance || '',
          ai_prompt: dossie.ai_prompt || '',
          analysis_guidance: dossie.analysis_guidance || '',
          status: dossie.status || 'DRAFT'
        }
      : this.emptyDossieForm();
    this.showDossieModal = true;
  }

  closeDossieModal() {
    if (this.saving) return;
    this.showDossieModal = false;
    this.editingDossie = null;
  }

  saveDossie() {
    if (!this.tenantId || !this.userId) return;
    if (!this.dossieForm.title.trim() || !this.dossieForm.objective.trim()) {
      this.showMessage('Informe título e objetivo do dossiê.');
      return;
    }
    this.saving = true;
    const onSaved = () => {
      this.showMessage(this.editingDossie ? 'Dossiê atualizado.' : 'Dossiê criado com pasta no ControlDocs.');
      this.closeDossieModal();
      this.loadAll();
    };
    const onError = (error: any) => this.showMessage(error?.error?.error || 'Não foi possível salvar o dossiê.');
    const onComplete = () => this.saving = false;

    if (this.editingDossie) {
      this.dossieService.updateDossie(this.tenantId, this.userId, this.editingDossie.id, this.dossieForm).subscribe({
        next: onSaved,
        error: onError,
        complete: onComplete
      });
      return;
    }

    this.dossieService.createDossie(this.tenantId, this.userId, this.dossieForm).subscribe({
      next: () => {
        this.showMessage('Dossiê criado com pasta no ControlDocs.');
        this.closeDossieModal();
        this.loadAll();
      },
      error: onError,
      complete: onComplete
    });
  }

  deleteDossie(dossie: ControlDossieCase) {
    if (!this.tenantId || !this.userId) return;
    const confirmDelete = window.confirm(`Excluir o dossiê "${dossie.title}"?`);
    if (!confirmDelete) return;
    this.dossieService.deleteDossie(this.tenantId, this.userId, dossie.id).subscribe({
      next: () => {
        if (this.selectedDossie?.id === dossie.id) {
          this.selectedDossie = null;
          this.evidences = [];
        }
        this.showMessage('Dossiê removido.');
        this.loadAll();
      },
      error: () => this.showMessage('Não foi possível remover o dossiê.')
    });
  }

  openEvidenceModal() {
    if (!this.selectedDossie) {
      this.showMessage('Selecione um dossiê primeiro.');
      return;
    }
    this.evidenceFiles = [];
    this.evidenceForm = this.emptyEvidenceForm();
    this.showEvidenceModal = true;
  }

  closeEvidenceModal() {
    if (this.saving) return;
    this.showEvidenceModal = false;
  }

  onEvidenceFileChange(event: Event) {
    const input = event.target as HTMLInputElement;
    this.evidenceFiles = Array.from(input.files || []);
    if (this.evidenceFiles.length === 1 && !this.evidenceForm.title) {
      this.evidenceForm.title = this.evidenceFiles[0].name;
    }
    if (this.evidenceFiles.length > 1 && !this.evidenceForm.title) {
      this.evidenceForm.title = `${this.evidenceFiles.length} evidências`;
    }
    const detectedType = this.detectEvidenceType(this.evidenceFiles);
    if (detectedType) {
      this.evidenceForm.evidence_type = detectedType;
    }
  }

  saveEvidence() {
    if (!this.tenantId || !this.userId || !this.selectedDossie) return;
    if (!this.evidenceForm.title.trim() && !this.evidenceFiles.length) {
      this.showMessage('Informe o título da evidência ou selecione arquivos.');
      return;
    }
    this.saving = true;
    this.dossieService.addEvidence(this.tenantId, this.userId, this.selectedDossie.id, {
      ...this.evidenceForm,
      files: this.evidenceFiles
    }).subscribe({
      next: () => {
        this.showMessage('Evidência adicionada e vinculada à pasta do ControlDocs.');
        this.closeEvidenceModal();
        this.loadAll();
        this.loadEvidences();
      },
      error: (error) => this.showMessage(error?.error?.error || 'Não foi possível adicionar a evidência.'),
      complete: () => this.saving = false
    });
  }

  deleteEvidence(evidence: ControlDossieEvidence) {
    if (!this.tenantId || !this.userId) return;
    this.dossieService.deleteEvidence(this.tenantId, this.userId, evidence.id).subscribe({
      next: () => {
        this.showMessage('Evidência removida.');
        this.loadEvidences();
        this.loadAll();
      },
      error: () => this.showMessage('Não foi possível remover a evidência.')
    });
  }

  analyzeSelected() {
    if (!this.tenantId || !this.userId || !this.selectedDossie || this.analyzing) return;
    this.analyzing = true;
    this.dossieService.analyze(this.tenantId, this.userId, this.selectedDossie.id, this.analysisGuidanceDraft).subscribe({
      next: (response) => {
        if (this.selectedDossie) {
          this.selectedDossie = {
            ...this.selectedDossie,
            status: 'ORGANIZED',
            final_report: response.final_report || this.selectedDossie.final_report,
            final_summary: response.final_summary || this.selectedDossie.final_summary,
            analysis_guidance: response.analysis_guidance ?? this.analysisGuidanceDraft,
            last_analyzed_at: response.last_analyzed_at || this.selectedDossie.last_analyzed_at
          };
          this.reportDraft = this.selectedDossie.final_report || '';
        }
        this.showMessage(response.message || 'Análise concluída.');
        this.refreshSelectedDossie();
        this.loadEvidences();
        this.loadAll();
      },
      error: (error) => this.showMessage(error?.error?.error || 'Não foi possível solicitar análise.'),
      complete: () => this.analyzing = false
    });
  }

  startEditReport() {
    if (!this.selectedDossie?.final_report) return;
    this.reportDraft = this.selectedDossie.final_report;
    this.editingReport = true;
  }

  cancelEditReport() {
    this.reportDraft = this.selectedDossie?.final_report || '';
    this.editingReport = false;
  }

  saveReport(status = 'ORGANIZED') {
    if (!this.tenantId || !this.userId || !this.selectedDossie || this.saving) return;
    if (!this.reportDraft.trim()) {
      this.showMessage('Informe o conteúdo do relatório.');
      return;
    }
    this.saving = true;
    this.dossieService.updateReport(this.tenantId, this.userId, this.selectedDossie.id, this.reportDraft, status).subscribe({
      next: (response) => {
        if (this.selectedDossie) {
          this.selectedDossie = {
            ...this.selectedDossie,
            final_report: response.final_report,
            final_summary: response.final_summary || this.selectedDossie.final_summary,
            status: response.status
          };
        }
        this.editingReport = false;
        this.showMessage(status === 'FINALIZED' ? 'Relatório salvo e dossiê finalizado.' : 'Relatório salvo.');
        this.loadAll();
      },
      error: (error) => this.showMessage(error?.error?.error || 'Não foi possível salvar o relatório.'),
      complete: () => this.saving = false
    });
  }

  finalizeSelected() {
    if (!this.tenantId || !this.userId || !this.selectedDossie || this.saving) return;
    if (this.editingReport) {
      this.saveReport('FINALIZED');
      return;
    }
    this.saving = true;
    this.dossieService.finalizeDossie(this.tenantId, this.userId, this.selectedDossie.id).subscribe({
      next: (response) => {
        if (this.selectedDossie) {
          this.selectedDossie = { ...this.selectedDossie, status: response.status };
        }
        this.showMessage(response.message || 'Dossiê finalizado.');
        this.loadAll();
      },
      error: (error) => this.showMessage(error?.error?.error || 'Não foi possível finalizar o dossiê.'),
      complete: () => this.saving = false
    });
  }

  exportReport() {
    if (!this.selectedDossie?.final_report) return;
    const filename = `${this.slugify(this.selectedDossie.title)}-relatorio.md`;
    const blob = new Blob([this.selectedDossie.final_report], { type: 'text/markdown;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    link.click();
    URL.revokeObjectURL(url);
  }

  formatDate(value?: string | null) {
    if (!value) return '-';
    return new Date(value).toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'short' });
  }

  statusLabel(status?: string) {
    return this.statusLabels[status || ''] || status || '-';
  }

  evidenceTypeLabel(type?: string) {
    return this.evidenceTypeLabels[type || ''] || type || '-';
  }

  evidenceFileLabel() {
    if (!this.evidenceFiles.length) {
      return 'Clique para anexar prints, áudios, PDFs ou arquivos';
    }
    if (this.evidenceFiles.length === 1) {
      return this.evidenceFiles[0].name;
    }
    return `${this.evidenceFiles.length} arquivos selecionados`;
  }

  private emptyDossieForm() {
    return {
      title: '',
      objective: '',
      evidence_guidance: '',
      ai_prompt: '',
      analysis_guidance: '',
      status: 'DRAFT'
    };
  }

  private emptyEvidenceForm() {
    return {
      title: '',
      evidence_type: 'TEXT',
      description: '',
      text_content: '',
      source_url: '',
      facts_date: this.todayInputDate()
    };
  }

  private detectEvidenceType(files: File[]) {
    if (!files.length) return '';
    const detected = files.map((file) => {
      const mime = String(file.type || '').toLowerCase();
      const name = String(file.name || '').toLowerCase();
      if (mime.startsWith('image/') || /\.(png|jpe?g|webp|gif|heic|heif)$/.test(name)) return 'IMAGE';
      if (mime.startsWith('audio/') || /\.(mp3|m4a|wav|ogg|aac|opus)$/.test(name)) return 'AUDIO';
      if (mime.startsWith('video/') || /\.(mp4|mov|avi|mkv|webm)$/.test(name)) return 'VIDEO';
      if (mime.includes('pdf') || name.endsWith('.pdf')) return 'PDF';
      if (mime || /\.[a-z0-9]+$/.test(name)) return 'DOCUMENT';
      return 'OTHER';
    });
    const unique = Array.from(new Set(detected));
    return unique.length === 1 ? unique[0] : 'OTHER';
  }

  private todayInputDate() {
    const today = new Date();
    const year = today.getFullYear();
    const month = String(today.getMonth() + 1).padStart(2, '0');
    const day = String(today.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  private isAllowedProfile(user: any) {
    const roleName = String(user?.role_name || user?.role?.name || user?.roleName || '').toLowerCase();
    return roleName.includes('master') || roleName.includes('admin') || Number(user?.role_id) === 1;
  }

  private showMessage(message: string) {
    this.snackBar.open(message, 'Fechar', { duration: 3500 });
  }

  private slugify(value: string) {
    return String(value || 'dossie')
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/[^a-zA-Z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '')
      .toLowerCase() || 'dossie';
  }
}
